import { alpha, createTheme } from '@mui/material/styles';

const primaryMain = '#e86026';
const primaryDark = '#c94b19';
const primaryLight = '#f19a72';
const accentMain = '#656e8f';
const borderColor = '#ecd6ca';

const appTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: primaryMain,
      dark: primaryDark,
      light: primaryLight,
      contrastText: '#ffffff',
    },
    secondary: {
      main: accentMain,
      dark: '#d56f2f',
      light: '#f7b07f',
      contrastText: '#ffffff',
    },
    background: {
      default: '#fdf5ef',
      paper: '#ffffff',
    },
    text: {
      primary: '#3b302f',
      secondary: '#7f746e',
    },
    success: {
      main: '#2ec58d',
    },
    warning: {
      main: '#ffbf66',
    },
    error: {
      main: '#ff6b8b',
    },
    divider: borderColor,
  },
  shape: {
    borderRadius: 18,
  },
  typography: {
    fontFamily: '"Segoe UI", "Helvetica Neue", Arial, sans-serif',
    h4: {
      fontWeight: 800,
      letterSpacing: '-0.03em',
    },
    h5: {
      fontWeight: 800,
      letterSpacing: '-0.02em',
    },
    h6: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    button: {
      fontWeight: 700,
      textTransform: 'none',
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          colorScheme: 'light',
        },
        body: {
          background:
            'linear-gradient(180deg, #fff8f3 0%, #f8f0ea 45%, #f5ece7 100%)',
          color: '#3b302f',
          backgroundAttachment: 'fixed',
          scrollbarColor: `${alpha(primaryMain, 0.45)} transparent`,
        },
        '#root': {
          minHeight: '100vh',
        },
        '*': {
          scrollbarWidth: 'thin',
        },
        '*::-webkit-scrollbar': {
          width: 10,
          height: 10,
        },
        '*::-webkit-scrollbar-thumb': {
          background:
            'linear-gradient(180deg, rgba(232,96,38,0.55) 0%, rgba(241,154,114,0.48) 100%)',
          borderRadius: 999,
        },
        '*::-webkit-scrollbar-track': {
          background: 'rgba(255,255,255,0.42)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          display: 'none',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        regular: {
          minHeight: 12,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          background:
            'linear-gradient(180deg, #656e8f 0%, #59617d 42%, #4c546d 100%)',
          borderRight: 'none',
          backdropFilter: 'blur(18px)',
          color: '#ffffff',
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#ffffff',
          border: `1px solid ${alpha('#d9e1ee', 0.9)}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          border: `1px solid ${alpha('#d9e1ee', 0.9)}`,
          background: '#ffffff',
          boxShadow: '0 16px 36px rgba(101, 110, 143, 0.10)',
          transition: 'transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 20,
          '&:last-child': {
            paddingBottom: 20,
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 14,
          paddingInline: 18,
          paddingBlock: 10,
        },
        outlined: {
          borderWidth: 1.5,
          backgroundColor: alpha('#ffffff', 0.88),
          backdropFilter: 'blur(10px)',
        },
        containedPrimary: {
          backgroundImage: `linear-gradient(135deg, ${primaryDark} 0%, ${primaryMain} 100%)`,
        },
        containedSecondary: {
          backgroundImage: `linear-gradient(135deg, ${accentMain} 0%, #f4b067 100%)`,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 14,
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          boxShadow: '0 12px 30px rgba(232, 96, 38, 0.18)',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#ffffff', 0.96),
          borderRadius: 14,
          '& fieldset': {
            borderColor: alpha('#cdd7e7', 0.9),
          },
          '&:hover fieldset': {
            borderColor: alpha(primaryMain, 0.34),
          },
        },
      },
    },
    MuiFormControl: {
      styleOverrides: {
        root: {
          minWidth: 0,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontWeight: 600,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          minWidth: 220,
          borderRadius: 14,
          backgroundColor: alpha('#ffffff', 0.96),
          boxShadow: '0 8px 20px rgba(101, 110, 143, 0.08)',
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: alpha(primaryMain, 0.34),
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: primaryDark,
          },
        },
        select: {
          borderRadius: 14,
          minHeight: 44,
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'transparent',
        },
        icon: {
          color: primaryMain,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 18,
          border: `1px solid ${alpha('#d9e1ee', 0.9)}`,
          background: '#ffffff',
          boxShadow: '0 20px 48px rgba(101, 110, 143, 0.12)',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          marginInline: 6,
          marginBlock: 2,
          '&.Mui-selected': {
            backgroundColor: alpha(primaryMain, 0.10),
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 22,
          overflow: 'hidden',
          border: `1px solid ${alpha(primaryMain, 0.08)}`,
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(247,252,249,0.96) 100%)',
          boxShadow: '0 20px 50px rgba(101, 110, 143, 0.10)',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          borderSpacing: 0,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          background:
            `linear-gradient(135deg, ${alpha(primaryMain, 0.10)} 0%, ${alpha(primaryLight, 0.12)} 100%)`,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: 'transparent',
          color: primaryDark,
          fontWeight: 700,
          paddingTop: 18,
          paddingBottom: 18,
          borderBottom: `1px solid ${alpha(primaryMain, 0.10)}`,
        },
        body: {
          borderColor: alpha(primaryMain, 0.08),
          paddingTop: 16,
          paddingBottom: 16,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 160ms ease, transform 160ms ease, box-shadow 160ms ease',
          '&:nth-of-type(even)': {
            backgroundColor: alpha(primaryMain, 0.018),
          },
          '&:hover': {
            backgroundColor: alpha(primaryMain, 0.06),
            boxShadow: `inset 3px 0 0 ${alpha(primaryMain, 0.55)}`,
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          fontWeight: 700,
        },
        icon: {
          color: `${primaryMain} !important`,
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 52,
        },
        flexContainer: {
          gap: 8,
        },
        indicator: {
          height: 4,
          borderRadius: 999,
          backgroundImage: `linear-gradient(90deg, ${primaryMain} 0%, ${primaryLight} 100%)`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 28,
          background:
            'linear-gradient(180deg, rgba(255,255,255,0.99) 0%, rgba(255,247,242,0.98) 100%)',
          boxShadow: '0 28px 80px rgba(101, 110, 143, 0.18)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontWeight: 800,
          color: primaryDark,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          paddingTop: 10,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 20,
          gap: 10,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 52,
          fontWeight: 700,
          textTransform: 'none',
          borderRadius: 14,
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontWeight: 800,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 12,
          backgroundColor: alpha(primaryDark, 0.94),
          backdropFilter: 'blur(8px)',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 700,
          backgroundImage:
            'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(255,247,242,0.94) 100%)',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: {
          borderRadius: 999,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          boxShadow: '0 18px 40px rgba(101, 110, 143, 0.14)',
        },
      },
    },
  },
});

export default appTheme;
